// ────────────────────────────────────────────────────────────────────────────
// myI route recommendation.
//
// Given an order's crossing (origin/destination country) it ranks the
// ORGANISATION'S OWN route picks — the same catalogue the order form books
// from, under either key style — and suggests the best one plus ranked
// alternatives. Deterministic, held in the worklist memo, nothing persisted
// until the dispatcher books. The sailings themselves are read from the ferry
// service when the panel opens (see `SailingOptions`); here a route is timed on
// the latest the truck could still sail and deliver on time. The indicative
// cost and short label still come from the placeholder table until a rating
// source exists.
// ────────────────────────────────────────────────────────────────────────────

import type { OrganisationRoute } from '@my/directory-api';
import { estimatedRoadKm, roadMinutesFor } from '../../../utils/roadDistance';
import { portCoordinatesByName } from '../../../utils/portCoordinates';
import type { CrossingCarrier, CrossingRoute } from '../../ferry/catalogue';
import { toLandmass } from './crossing';
import { FERRY_ROUTES } from './ferryTimetable';
import type { CandidateRoute, RouteOption } from './mockData';
import { canonicalPort } from './ports';

export interface RouteRecommendation {
  best: RouteOption;
  /** Ranked alternatives (capped) for the "+N alt" affordance. */
  alts: RouteOption[];
  /** Minutes until the latest port check-in that still delivers on time;
   *  ≤ 0 = already missed, undefined when no fit context was supplied. */
  cutoffMinutes?: number;
}

/** Delivery/clock context so the recommendation can prefer a catchable route
 *  and compute its cutoff. All times are minutes from today-midnight. */
export interface RouteFit {
  /** Delivery deadline (window time, or end-of-day when "Any"). */
  deadlineMin: number;
  /** "Now" — drives the cutoff countdown. */
  nowAbsMin: number;
  customs: boolean;
  /**
   * Where the delivery actually is, so the road leg is measured from the
   * arrival port to THIS drop — which is the whole reason one port beats
   * another.
   */
  destination?: { latitude?: number | null; longitude?: number | null } | null;
  /**
   * Real truck driving times for an arrival-port-to-delivery leg, when the
   * caller has them. Absent on the first pass, which decides WHICH ports are
   * worth asking a metered routing API about.
   */
  resolveRoad?: (portName: string) => { minutes: number; km: number } | null;
}

/** An organisation pick resolved against the merged catalogue. */
export interface ResolvedPick {
  row: OrganisationRoute;
  entry: CrossingRoute;
}

const MAX_ALTS = 2;
/** For a route whose catalogue entry carries no crossing time. */
const DEFAULT_CROSSING_MIN = 4 * 60;
const CUSTOMS_CLEAR_MIN = 45;

/** One direction of a pick, with the ports as sailed. */
interface Leg {
  entry: CrossingRoute;
  candidate: CandidateRoute;
  originPort: string;
  destinationPort: string;
  originTerritory: string | null | undefined;
  destinationTerritory: string | null | undefined;
  operator: string;
}

/** A catalogue-file entry is sailed both ways; a Ferry entry is one direction. */
function legsOf(pick: ResolvedPick, carriers: Map<string, CrossingCarrier>): Leg[] {
  const { row, entry } = pick;
  const operator = carriers.get(entry.carrierKey)?.name ?? entry.carrierKey;
  const forward: Leg = {
    entry,
    candidate: {
      catalogueKey: row.catalogueKey,
      orgRouteId: row.id,
      reversed: false,
      preferred: row.isPreferred,
    },
    originPort: entry.originPortName,
    destinationPort: entry.destinationPortName,
    originTerritory: entry.originCustomsTerritory ?? entry.originCountryIsoCode,
    destinationTerritory: entry.destinationCustomsTerritory ?? entry.destinationCountryIsoCode,
    operator,
  };
  if (!entry.bidirectional) return [forward];
  return [
    forward,
    {
      ...forward,
      candidate: { ...forward.candidate, reversed: true },
      originPort: entry.destinationPortName,
      destinationPort: entry.originPortName,
      originTerritory: forward.destinationTerritory,
      destinationTerritory: forward.originTerritory,
    },
  ];
}

/** "Rosslare Europort" → "Ross", the queue's route headline when the placeholder table has no lane. */
function shortPort(name: string): string {
  const first = name.trim().split(/[\s(]/)[0] ?? name;
  return first.slice(0, 4);
}

/** The placeholder table's lane for a port pair: its short label and indicative cost. */
function laneFor(leg: Leg) {
  const dep = canonicalPort(leg.originPort);
  const arr = canonicalPort(leg.destinationPort);
  return FERRY_ROUTES.find(
    (r) => canonicalPort(r.departurePort) === dep && canonicalPort(r.arrivalPort) === arr,
  );
}

interface RoadLegFigure {
  km: number;
  /** Routed driving minutes, or undefined when this is an estimate. */
  minutes?: number;
  estimated: boolean;
}

/**
 * The arrival-port-to-delivery road leg, in km. A routed answer wins; else a
 * straight-line estimate from the port table (see `utils/roadDistance`); else
 * the placeholder table's indicative figure.
 */
function roadLegFor(leg: Leg, fit: RouteFit | undefined): RoadLegFigure {
  const routed = fit?.resolveRoad?.(leg.destinationPort);
  if (routed) return { km: routed.km, minutes: routed.minutes, estimated: false };
  const port = portCoordinatesByName(leg.destinationPort);
  const measured = fit?.destination ? estimatedRoadKm(port ?? {}, fit.destination) : null;
  return { km: measured ?? laneFor(leg)?.indicativeRoadKm ?? 0, estimated: true };
}

function toRouteOption(leg: Leg, road: RoadLegFigure): RouteOption {
  const lane = laneFor(leg);
  return {
    routeShort:
      lane?.routeShort ?? `${shortPort(leg.originPort)}→${shortPort(leg.destinationPort)}`,
    departurePort: leg.originPort,
    arrivalPort: leg.destinationPort,
    operator: leg.operator,
    roadKm: road.km,
    roadMinutes: road.minutes,
    roadEstimated: road.estimated,
    // Indicative until a rating source exists; the queue hides a zero.
    cost: lane?.indicativeCost ?? 0,
    type: 'Off-peak',
    candidate: leg.candidate,
  };
}

interface RouteTiming {
  /** Minutes until the latest check-in that still delivers on time. */
  cutoffMin: number;
  feasible: boolean;
}

/**
 * Time a route against the delivery deadline without a timetable: the latest
 * departure is the deadline less the crossing, customs and the drive; the
 * cutoff is that departure less the route's check-in, counted from now.
 */
function timeLeg(leg: Leg, fit: RouteFit, road: RoadLegFigure): RouteTiming {
  const checkIn = leg.entry.typicalCheckInMinutes ?? (fit.customs ? 5 * 60 : 90);
  const crossing = leg.entry.typicalDurationMinutes ?? DEFAULT_CROSSING_MIN;
  const transit =
    crossing + (fit.customs ? CUSTOMS_CLEAR_MIN : 0) + (road.minutes ?? roadMinutesFor(road.km));
  const cutoffMin = fit.deadlineMin - transit - checkIn - fit.nowAbsMin;
  return { cutoffMin, feasible: cutoffMin > 0 };
}

/**
 * Recommend a crossing for an origin→destination country pair from the
 * organisation's picks, or `null` when there's no crossing (same landmass) or
 * no pick serves it. Starred picks first, then those still catchable for the
 * deadline, then the shortest road leg, then the label (stable tiebreak).
 */
export function recommendRoute(
  originIso: string | null | undefined,
  destIso: string | null | undefined,
  picks: readonly ResolvedPick[],
  carriers: Map<string, CrossingCarrier>,
  fit?: RouteFit,
): RouteRecommendation | null {
  const origin = toLandmass(originIso);
  const dest = toLandmass(destIso);
  if (origin === null || dest === null || origin === dest) return null;

  // Matched on landmass, as the order's journey planner does: Belfast is GB by
  // its code but XI by territory, and it is the territory that puts it on the
  // island of Ireland.
  const matches = picks
    .flatMap((pick) => legsOf(pick, carriers))
    .filter(
      (leg) =>
        toLandmass(leg.originTerritory) === origin && toLandmass(leg.destinationTerritory) === dest,
    );
  if (matches.length === 0) return null;

  const ranked = matches
    .map((leg) => {
      const road = roadLegFor(leg, fit);
      return { leg, road, timing: fit ? timeLeg(leg, fit, road) : null };
    })
    .sort((a, b) => {
      const pa = a.leg.candidate.preferred ? 0 : 1;
      const pb = b.leg.candidate.preferred ? 0 : 1;
      const fa = a.timing && !a.timing.feasible ? 1 : 0;
      const fb = b.timing && !b.timing.feasible ? 1 : 0;
      return (
        pa - pb ||
        fa - fb ||
        a.road.km - b.road.km ||
        `${a.leg.originPort}${a.leg.destinationPort}`.localeCompare(
          `${b.leg.originPort}${b.leg.destinationPort}`,
        )
      );
    });

  const best = ranked[0];
  if (!best) return null;

  return {
    best: toRouteOption(best.leg, best.road),
    alts: ranked.slice(1, 1 + MAX_ALTS).map((r) => toRouteOption(r.leg, r.road)),
    cutoffMinutes: best.timing ? best.timing.cutoffMin : undefined,
  };
}
